import { useMemo, useState, KeyboardEvent, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PasscodeList } from '../../../types/working-model';
import Icon, { COLORS, ICONS } from '../../general-ui/icon';
import LargeButton from '../../general-ui/button/large-button';
import Checkbox from '../../general-ui/checkbox/checkbox';
import { EventsState } from '../../../store/types';
import Modal from '../../general-ui/modal/modal';
import TextInput, { Validation } from '../../general-ui/text-input/text';
import PasscodeListMember from './passcode-list-member';
import WaitingIndicator from "../../general-ui/waiting-indicator/waiting-indicator";
import {
	getPasscodeListMembers,
	searchMembers,
	searchMemberCount,
	removeAllMembers,
	addSelectedMember,
	// removeSelectedMember
} from '../../../store/actions/admin/passcode-list-members';
import SearchBar from './search';
import { AddNewMember } from '../../../connection/passcode-list-members';
import { WorkingPasscodeList } from '../create/registration/passcode-list';
import { GetDefaultPasscodeList } from '../../../store/utils/create-event';
import { isValidEmail } from '../../../utils/utils';
import {
	UpdateMaxUses,
	UpdateMasterPasscode,
	UploadMembers,
	DeleteMembers
} from '../../../connection/passcode-lists';
import { getPasscodeLists } from '../../../store/actions/admin/passcode-lists';
import {
	GetUploadEndpoint,
	MakeItemPublic,
	UploadToS3,
} from '../../../connection/uploads';
import { v4 as uuid } from 'uuid';
import Switch from '../../general-ui/switch/switch';
import Paging from '../../general-ui/paging/paging';
// import { removeAll } from '../../../utils/event-emitter';

export interface PasscodeListEditorProps {
	currentPasscode: PasscodeList | null;
	onClick: () => void;
}

interface Passcode {
	passcode: string;
	email: string;
	emailValidationState?: Validation;
}

enum PASSCODE_MEMBER_ORDER {
	UNORDERED = 0,
	EMAIL_ASC,
	EMAIL_DESC,
	PASSCODE_ASC,
	PASSCODE_DESC
}

export default function PasscodeListEditor(
	props: PasscodeListEditorProps
): JSX.Element {
	const { currentPasscode, onClick } = props;
	const {
		PasscodeListMembersReducer: { members, member_count, loadingMembers },
		AuthReducer: { token, user },
	} = useSelector((state: EventsState) => state);
	const dispatch = useDispatch();

	const [currentPasscodePage, setCurrentPasscodePage] = useState(1);
	const [maxPage, setMaxPage] = useState(1);
	const [passcodeModalOpen, setPasscodeModalOpen] = useState(false);
	const [deletePasscodesModalOpen, setDeletePasscodesModalOpen] = useState(false);
	const [allChecked, setAllChecked] = useState(false);
	const [settingsModalOpen, setSettingsModalOpen] = useState(false);
	const [searchValue, setSearchValue] = useState('');
	const [passcodesToAdd, setPasscodesToAdd] = useState<Passcode[]>([
		{ passcode: '', email: '' },
	]);
	const [newUses/* , setNewUses */] = useState<number | null>(null);
	const [newPasscode, setNewPasscode] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [workingPasscodeList, setWorkingPasscodeList] = useState<
		WorkingPasscodeList
	>(GetDefaultPasscodeList());
	const [hasFile, setHasFile] = useState(false);
	const [fileName, setFilename] = useState('');
	const [uploadToggle, setUploadToggle] = useState(false);
	const [order, setOrder] = useState(PASSCODE_MEMBER_ORDER.UNORDERED);
	const [isDeleting, setisDeleting] = useState(false);


	function handleAllCheck() {
		if (allChecked === false) {
			members.forEach((member) => {
				dispatch(addSelectedMember(member));
			});
		} else {
			dispatch(removeAllMembers());
		}
		setAllChecked(prev => !prev);
	}


	useEffect(() => {
		if (currentPasscode) {
			setMaxPage(Math.ceil(currentPasscode.member_count / 50));
		}
	}, [currentPasscode]);

	useEffect(() => {
		if (currentPasscode?.passcode_list) {
			dispatch(
				searchMembers(
					token!,
					currentPasscode?.passcode_list,
					currentPasscodePage - 1,
					searchValue,
					order
				)
			);
			dispatch(
				removeAllMembers()
			);
			setAllChecked(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentPasscode?.passcode_list, currentPasscodePage, dispatch, token, order]);

	const {
		PasscodeListMembersReducer: { selectedMembers },
	} = useSelector((state: EventsState) => state);

	function handleSearchValue(e: React.ChangeEvent<HTMLInputElement>) {
		setSearchValue(e.target.value);
		dispatch(
			removeAllMembers()
		);
		setAllChecked(false);
	}

	function handleSearch() {
		setCurrentPasscodePage(1);
		dispatch(
			searchMembers(
				token!,
				Number(currentPasscode?.passcode_list),
				currentPasscodePage - 1,
				searchValue,
				order
			)
		);
		dispatch(
			searchMemberCount(
				token!,
				Number(currentPasscode?.passcode_list),
				searchValue
			)
		);
	}

	useEffect(() => {
		setMaxPage(Math.ceil(member_count / 50));
	}, [member_count]);

	const handleKeyPress = ({ key }: KeyboardEvent) => {
		switch (key) {
			case 'Enter':
				setCurrentPasscodePage(1);
				return handleSearch();
			case 'Escape': {
				if (currentPasscode?.passcode_list) {
					setCurrentPasscodePage(1);
					return dispatch(
						getPasscodeListMembers(
							token!,
							currentPasscode?.passcode_list,
							0,
							order
						)
					);
				} else {
					return;
				}
			}

			default:
				return;
		}
	};

	const setPasscode = useCallback((index: number) => {
		return (e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;
			const array: Passcode[] = [...passcodesToAdd];
			const item = { ...array[index] };
			item.passcode = value.trim();
			array[index] = item;
			setPasscodesToAdd(array);
		};
	}, [passcodesToAdd]);

	const setEmail = useCallback((index: number) => {
		return (e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;
			const array: Passcode[] = [...passcodesToAdd];
			const item = { ...array[index] };
			item.email = value.trim();
			item.emailValidationState = (value === '' || isValidEmail(value.trim())) ? Validation.normal : Validation.error;
			array[index] = item;
			setPasscodesToAdd(array);
		};
	}, [passcodesToAdd]);

	function addPasscodeToAdd() {
		if (passcodesToAdd.length > 10) {
			return;
		} else {
			const passcode: Passcode = { passcode: '', email: '' };
			const array: Passcode[] = [...passcodesToAdd];
			array.push(passcode);
			return setPasscodesToAdd(array);
		}
	}

	async function submitPasscodesToAdd() {

		if (hasFile) {
			setLoading(true);
			await UploadMembers(
				token!,
				user!.active_channel,
				fileName,
				Number(currentPasscode!.passcode_list!),
				Number(currentPasscode?.type),
				workingPasscodeList.url
			);
		}

		if (currentPasscode?.passcode_list) {
			await Promise.all(passcodesToAdd.map(async (passcode: Passcode) => {
				await AddNewMember(
					token!,
					currentPasscode.passcode_list,
					passcode.passcode,
					passcode.email
				);
			}));

			setCurrentPasscodePage(1);
			dispatch(
				getPasscodeListMembers(
					token!,
					currentPasscode?.passcode_list,
					0,
					order
				)
			);
			dispatch(getPasscodeLists(user!.active_channel, token!));
		}

		setLoading(false);
		setPasscodeModalOpen(false);
		setPasscodesToAdd([{ passcode: '', email: '' }]);
		setHasFile(false);
		return;
	}

	async function submitPasscodesToRemove() {
		setisDeleting(true);
		setLoading(true);
		await DeleteMembers(selectedMembers, token!);
		const passcodeMembersOffset = 0;

		if (currentPasscode?.passcode_list) {
			setCurrentPasscodePage(1);
			dispatch(
				getPasscodeListMembers(
					token!,
					currentPasscode?.passcode_list,
					passcodeMembersOffset,
					order
				)
			);
			dispatch(getPasscodeLists(user!.active_channel, token!));
		}

		setAllChecked(false);
		dispatch(removeAllMembers());
		setDeletePasscodesModalOpen(false);
		setLoading(false);
		setisDeleting(false);
	}

	function closeModal() {
		setPasscodeModalOpen(false);
		setDeletePasscodesModalOpen(false);
		setPasscodesToAdd([{ passcode: '', email: '' }]);
	}

	const removePasscodeToAdd = useCallback((index: number) => {
		const array: Passcode[] = [...passcodesToAdd];
		array.splice(index, 1);
		return setPasscodesToAdd(array);
	}, [passcodesToAdd]);

	function handleArrowClick(arrowClicked: string): void {

		switch (arrowClicked) {
			case 'PASSCODE': {
				order === PASSCODE_MEMBER_ORDER.PASSCODE_ASC ? setOrder(PASSCODE_MEMBER_ORDER.PASSCODE_DESC) : setOrder(PASSCODE_MEMBER_ORDER.PASSCODE_ASC);
				break;
			}
			case 'EMAIL': {
				order === PASSCODE_MEMBER_ORDER.EMAIL_ASC ? setOrder(PASSCODE_MEMBER_ORDER.EMAIL_DESC) : setOrder(PASSCODE_MEMBER_ORDER.EMAIL_ASC);
				break;
			}
			default: {
				setOrder(PASSCODE_MEMBER_ORDER.UNORDERED);
			}
		}
	}

	useEffect(() => {
		if (searchValue === '') {
			if (currentPasscode?.passcode_list) {
				setCurrentPasscodePage(1);
				dispatch(
					searchMemberCount(
						token!,
						Number(currentPasscode?.passcode_list),
						searchValue
					)
				);
				dispatch(
					searchMembers(
						token!,
						currentPasscode?.passcode_list,
						0,
						searchValue,
						order
					)
				);
			}
		}
	}, [currentPasscode?.passcode_list, dispatch, searchValue, token, order]);

	const isValid = useMemo(() => {
		switch (currentPasscode?.type) {
			case 2: {
				return (
					hasFile ||
					passcodesToAdd.every(
						(passcode) => isValidEmail(passcode.email) && passcode.passcode
					) && passcodesToAdd.length !== 0
				);
			}
			case 1: {
				return (
					hasFile ||
					passcodesToAdd.every((passcode) => passcode.passcode) && passcodesToAdd.length !== 0
				);
			}
			case 0: {
				return (
					hasFile || passcodesToAdd.every((passcode) => isValidEmail(passcode.email)) && passcodesToAdd.length !== 0
				);
			}
			default: {
				return (
					hasFile ||
					passcodesToAdd.every(
						(passcode) => (isValidEmail(passcode.email) && passcode.passcode) && passcodesToAdd.length !== 0
					)
				);
			}
		}
	}, [currentPasscode?.type, hasFile, passcodesToAdd]);

	const uploadFile = useMemo(() => {
		return currentPasscode?.member_count === 0 ? (
			<button
				className="empty-list-button"
				onClick={() => setPasscodeModalOpen(true)}
			>
				<div>
					<Icon
						name={ICONS.ADD}
						size={16}
						color={COLORS.DEFAULT_GRAY}
					/>
					Add New Passcode
				</div>
				<div>or drag and drop .CSV file here</div>
			</button>
		) : (
			<> </>
		);
	}, [currentPasscode?.member_count]);

	function updateList() {
		if (currentPasscode) {
			if (newUses) {
				UpdateMaxUses(token!, newUses, currentPasscode.passcode_list);
			}
			if (newPasscode) {
				UpdateMasterPasscode(
					token!,
					newPasscode,
					currentPasscode.passcode_list
				);
			}
			dispatch(getPasscodeLists(user!.active_channel, token!));
			setSettingsModalOpen(false);
		}
	}

	// commenting out instead of deleting because passcodes is under active development
	// function updateUses(e: React.ChangeEvent<HTMLInputElement>) {
	// 	return setNewUses(Number(e.target.value));
	// }

	function updatePasscode(e: React.ChangeEvent<HTMLInputElement>) {
		return setNewPasscode(e.target.value);
	}

	const getPasscode = useMemo(() => {
		if (newPasscode) { return newPasscode; }
		else if (currentPasscode?.master_passcode) { return currentPasscode?.master_passcode; }
		else { return ''; }
	}, [currentPasscode?.master_passcode, newPasscode]);

	// commenting out instead of deleting because passcodes is under active development
	// const getUses = useMemo(() => {
	// 	if (newUses) {return newUses.toString();}
	// 	else if (currentPasscode?.max_uses)
	// 		{return currentPasscode?.max_uses.toString();}
	// 	else {return '';}
	// }, [currentPasscode?.max_uses, newUses]);

	async function onFile(file: File | FileList) {
		if (file instanceof File) {
			setLoading(true);
			setFilename(file.name);
			const endpoint = await GetUploadEndpoint(
				token!,
				`${user!.active_channel}/${uuid()}`,
				file.name.replace(/[^0-9a-zA-Z.]/g, '-')
			);

			await UploadToS3(
				endpoint.url,
				file,
				endpoint.fields
			);
			await MakeItemPublic(token!, endpoint.fields.Key);

			setWorkingPasscodeList((list: WorkingPasscodeList) => ({
				...list,
				url: endpoint.url + '/' + endpoint.fields.Key,
			}));
			setLoading(false);
			setHasFile(true);
		}
	}

	const addPasscode = useMemo(() => {
		switch (currentPasscode?.type) {
			case 0: {
				return (
					<>
						Enter an email
						<div
							style={{
								color: 'GrayText',
								fontSize: 14,
								marginBottom: 10,
								marginTop: 10,
							}}
						>
							The user must enter this email to register
						</div>
						{passcodesToAdd.map(
							(passcode: Passcode, index: number) => {
								return (
									<div
										key={index}
										style={{
											display: 'flex',
											alignItems: 'center',
										}}
									>
										<TextInput
											label={'Email'}
											placeholder={'Enter email'}
											onChange={setEmail(index)}
											style={{ width: '80%' }}
											value={
												passcodesToAdd[index].email
											}
											valid={passcodesToAdd[index].emailValidationState}
										/>
										<button
											onClick={() =>
												removePasscodeToAdd(index)
											}
											style={{
												height: 20,
												backgroundColor: 'transparent',
												display: 'flex',
												alignItems: 'center',
											}}
										>
											<Icon
												name={ICONS.CLOSE}
												size={16}
												color={COLORS.DEFAULT_GRAY}
											/>
										</button>
									</div>
								);
							}
						)}
					</>
				);
			}
			case 1: {
				return (
					<>
						Enter a passcode
						<div
							style={{
								color: 'GrayText',
								fontSize: 14,
								marginBottom: 10,
								marginTop: 10,
							}}
						>
							The user must enter this passcode to register.
						</div>
						{passcodesToAdd.map(
							(passcode: Passcode, index: number) => {
								return (
									<div
										key={index}
										style={{
											display: 'flex',
											alignItems: 'center',
										}}
									>
										<TextInput
											label={'Passcode'}
											placeholder={'Enter passcode'}
											onChange={setPasscode(index)}
											style={{ width: '80%' }}
											value={
												passcodesToAdd[index].passcode
											}
										/>
										<button
											onClick={() =>
												removePasscodeToAdd(index)
											}
											style={{
												height: 20,
												backgroundColor: 'transparent',
												display: 'flex',
												alignItems: 'center',
											}}
										>
											<Icon
												name={ICONS.CLOSE}
												size={16}
												color={COLORS.DEFAULT_GRAY}
											/>
										</button>
									</div>
								);
							}
						)}
					</>
				);
			}
			case 2: {
				return (
					<>
						Enter a passcode and email
						<div
							style={{
								color: 'GrayText',
								fontSize: 14,
								marginBottom: 10,
								marginTop: 10,
							}}
						>
							The user must enter both the passcode and the email
							to register.
						</div>
						{passcodesToAdd.map(
							(passcode: Passcode, index: number) => {
								return (
									<div
										key={index}
										style={{
											display: 'flex',
											alignItems: 'center',
										}}
									>
										<TextInput
											label={'Passcode'}
											placeholder={'Enter passcode'}
											onChange={setPasscode(index)}
											style={{ width: 272 }}
											value={
												passcodesToAdd[index].passcode
											}
										/>
										<TextInput
											label={'Email'}
											placeholder={'Enter email'}
											onChange={setEmail(index)}
											style={{
												width: 272,
												marginLeft: 16,
											}}
											value={
												passcodesToAdd[index].email
											}
											valid={passcodesToAdd[index].emailValidationState}
										/>
										<button
											onClick={() =>
												removePasscodeToAdd(index)
											}
											style={{
												height: 20,
												backgroundColor: 'transparent',
												display: 'flex',
												alignItems: 'center',
											}}
										>
											<Icon
												name={ICONS.CLOSE}
												size={16}
												color={COLORS.DEFAULT_GRAY}
											/>
										</button>
									</div>
								);
							}
						)}
					</>
				);
			}
			default: {
				return (
					<>
						Enter a passcode and email
						<div
							style={{
								color: 'GrayText',
								fontSize: 14,
								marginBottom: 10,
								marginTop: 10,
							}}
						>
							The user must enter both the passcode and the email
							to register.
						</div>
						{passcodesToAdd.map(
							(passcode: Passcode, index: number) => {
								return (
									<div
										key={index}
										style={{
											display: 'flex',
											alignItems: 'center',
										}}
									>
										<TextInput
											label={'Passcode'}
											placeholder={'Enter passcode'}
											onChange={setPasscode(index)}
											style={{ width: 272 }}
											value={
												passcodesToAdd[index].passcode
											}
										/>
										<TextInput
											label={'Email'}
											placeholder={'Enter email'}
											onChange={setEmail(index)}
											style={{
												width: 272,
												marginLeft: 16,
											}}
											value={
												passcodesToAdd[index].email
											}
											valid={passcodesToAdd[index].emailValidationState}
										/>
										<button
											onClick={() =>
												removePasscodeToAdd(index)
											}
											style={{
												height: 20,
												backgroundColor: 'transparent',
												display: 'flex',
												alignItems: 'center',
											}}
										>
											<Icon
												name={ICONS.CLOSE}
												size={16}
												color={COLORS.DEFAULT_GRAY}
											/>
										</button>
									</div>
								);
							}
						)}
					</>
				);
			}
		}
	}, [currentPasscode?.type, passcodesToAdd, removePasscodeToAdd, setEmail, setPasscode]);

	const closePasscodeModal = useCallback(() => {
		setPasscodeModalOpen(false);
	}, []);

	const closeDeletePasscodeModal = useCallback(() => {
		setDeletePasscodesModalOpen(false);
	}, []);

	const closeSettingsModal = useCallback(() => {
		setSettingsModalOpen(false);
	}, []);

	return (
		<div className="passcode-wrapper">
			<div className="passcode-container">
				<div className="list-header">
					<div className="header-left">
						<button
							onClick={onClick}
							style={{ backgroundColor: 'transparent' }}
						>
							<Icon
								name={ICONS.ARROW_LEFT}
								size={16}
								color={COLORS.DEFAULT_GRAY}
							/>
						</button>
						<h2>{currentPasscode?.name}</h2>
					</div>
					<div className="header-right">
						<SearchBar
							style={{ marginBottom: 0 }}
							onChange={handleSearchValue}
							placeholder={'Search...'}
							onKeyPress={handleKeyPress}
							onSubmit={handleSearch}
						/>
						<button
							className="list-settings"
							onClick={() => setSettingsModalOpen(true)}
						>
							<div
								style={{
									marginTop: '2px'
								}}>
								<Icon
									name={ICONS.SETTINGS_TRANSPARENT}
									size={12}
									color={COLORS.DEFAULT_GRAY}
								/>
							</div>
						</button>
						<button
							className="small add-passcode"
							onClick={() => setPasscodeModalOpen(true)}
						>
							<Icon
								name={ICONS.ADD}
								size={12}
								color={COLORS.BLACK}
							/>
							Add passcode
						</button>
						<button
							className="small remove-passcodes"
							onClick={() => setDeletePasscodesModalOpen(true)}
							disabled={selectedMembers.length === 0}
						>
							<Icon
								name={ICONS.DELETE}
								size={12}
								color={COLORS.BLACK}
							/>
							Delete selected
						</button>
					</div>
				</div>
				<>
					<div className="list-container">
						<ul>
							<li className="list-item">
								<div className={'header'}>
									{currentPasscode!.member_count !== 0 &&
										<Checkbox
											size={16}
											checked={allChecked}
											value={'list-header-checkbox'}
											onChange={handleAllCheck}
											selectedIconName={
												ICONS.SQUARE_CHECKBOX_SELECTED
											}
											unselectedIconName={
												ICONS.SQUARE_CHECKBOX_UNSELECTED
											}
											transparent
										/>
									}
								</div>
								{currentPasscode?.type === 1 ||
									currentPasscode?.type === 2 ? (
									<div className={'header'}>
										<span>PASSCODE</span>
										<span
											className={'header-arrows'}
											onClick={() => (handleArrowClick('PASSCODE'))}
											style={{ cursor: "pointer" }}>
											<Icon
												name={ICONS.LITTLE_ARROW_UP}
												size={10}
												color={order === PASSCODE_MEMBER_ORDER.PASSCODE_ASC ? COLORS.CYAN : COLORS.WHITE}
											/>
											<Icon
												name={ICONS.LITTLE_ARROW_DOWN}
												size={10}
												color={order === PASSCODE_MEMBER_ORDER.PASSCODE_DESC ? COLORS.CYAN : COLORS.WHITE}
											/>
										</span>
									</div>
								) : (
									<></>
								)}
								{currentPasscode?.type === 0 ||
									currentPasscode?.type === 2 ? (
									<div className={'header'}>
										<span>EMAIL</span>
										<span
											className={'header-arrows'}
											onClick={() => (handleArrowClick('EMAIL'))}
											style={{ cursor: "pointer" }}>
											<Icon
												name={ICONS.LITTLE_ARROW_UP}
												size={10}
												color={order === PASSCODE_MEMBER_ORDER.EMAIL_ASC ? COLORS.CYAN : COLORS.WHITE}
											/>
											<Icon
												name={ICONS.LITTLE_ARROW_DOWN}
												size={10}
												color={order === PASSCODE_MEMBER_ORDER.EMAIL_DESC ? COLORS.CYAN : COLORS.WHITE}
											/>
										</span>
									</div>
								) : (
									<></>
								)}
							</li>
							{loadingMembers ?
								<div className={"people-loading"}>
									<WaitingIndicator />
								</div> :
								members.map((member: any) => {
									return (
										<PasscodeListMember
											key={member.passcode_list_member}
											member={member}
											type={Number(currentPasscode!.type!)}
										/>
									);
								})}
							<div>
								{uploadFile}
								{(!loadingMembers && currentPasscode!.member_count !== 0 && member_count > 0) &&
									<li className="list-pagination">
										<Paging
											page={currentPasscodePage}
											setPage={setCurrentPasscodePage}
											totalPages={maxPage}
										/>
									</li>
								}
								{!member_count && !loadingMembers && currentPasscode!.member_count !== 0 &&
									<div className={"people-loading"}>
										<span>No passcodes match your search.</span>
									</div>
								}
							</div>
						</ul>
					</div>
				</>
				<Modal
					open={passcodeModalOpen}
					onRequestClose={closePasscodeModal}
					title={'Add new passcode'}
					cancellable={false}
					closeable={false}
					footer={
						<>
							<button
								onClick={closeModal}
								style={{ marginRight: 16 }}
							>
								Cancel
							</button>
							<button
								disabled={loading || !isValid}
								onClick={submitPasscodesToAdd}
								className="primary"
							>
								Apply
							</button>
						</>
					}
				>
					<div>
						{addPasscode}
						{passcodesToAdd.length < 5 ? (
							<div>
								<button
									onClick={() => addPasscodeToAdd()}
									style={{
										backgroundColor: 'transparent',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										width: 100,
										height: 20,
										marginBottom: 20,
									}}
								>
									<Icon
										name={ICONS.ADD}
										size={18}
										color={COLORS.DEFAULT_GRAY}
									/>{' '}
									Add{' '}
								</button>
							</div>
						) : (
							<></>
						)}
						<div className="divider"></div>
						<div className="upload-toggle">
							<div>Upload list of users</div>
							<div style={{
								paddingTop: '15px',
								paddingLeft: '2px'
							}}>
								<Switch
									value={'upload-toggle'}
									on={uploadToggle}
									onClick={() =>
										setUploadToggle(!uploadToggle)
									}
								/>
							</div>
						</div>
						{uploadToggle ? (
							<div className="upload-button">
								<LargeButton
									title={'Upload file'}
									subtitle={
										'or drag and drop list here in CSV file format'
									}
									multiple={false}
									onFile={onFile}
									allowedFileTypes={[
										'text/csv',
										'application/vnd.ms-excel',
									]}
								/>
							</div>
						) : (
							<></>
						)}
					</div>
				</Modal>
				<Modal
					open={deletePasscodesModalOpen}
					onRequestClose={closeDeletePasscodeModal}
					title={'Confirm Delete'}
					cancellable={false}
					closeable={false}
					footer={
						<>
							<button
								onClick={closeModal}
								style={{ marginRight: 16 }}
								disabled={isDeleting}
							>
								Cancel
							</button>
							<button
								onClick={submitPasscodesToRemove}
								className="primary"
								disabled={isDeleting}
							>
								Apply
							</button>
						</>
					}
				>
					{!isDeleting ?
						<span>Are you sure you want to delete the selected passcodes?</span>
						:
						<div>
							<span>Delete in progress.</span>
							<span style={{ position: 'absolute', top: 135, left: 40 }}>
								<WaitingIndicator />
							</span>
						</div>
					}
				</Modal>
				<Modal
					open={settingsModalOpen}
					onRequestClose={closeSettingsModal}
					title={'Settings'}
					cancellable={true}
					closeable={false}
					size="normal"
					footer={
						<>
							<button
								onClick={closeSettingsModal}
								style={{ marginRight: 16 }}
							>
								Cancel
							</button>
							<button onClick={updateList} className="primary">
								Save
							</button>
						</>
					}
				>
					{currentPasscode ? (
						<div className="passcode-settings">
							<div className="divider"></div>
							<div>Master Passcode</div>
							<div>
								<TextInput
									label={
										'A code for admin users to use to gain access'
									}
									placeholder={'Enter Passcode'}
									defaultValue={getPasscode}
									onChange={updatePasscode}
								/>
							</div>
						</div>
					) : (
						<>Error loading passcode list</>
					)}
				</Modal>
			</div>
		</div>
	);
}
